import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Calendar, Save, AlertTriangle, Building2 } from 'lucide-react';

export default function OrganizerNewExhibition() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [venueCenters, setVenueCenters] = useState([]);
  const [filteredVenueCenters, setFilteredVenueCenters] = useState([]);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    country_id: '',
    city_id: '',
    venue_center_id: '',
    start_date: '',
    end_date: '',
    description: '',
    base_currency: 'TRY',
    default_price_per_sqm: '',
    default_ceiling_height: '',
    default_max_stand_height: ''
  });

  useEffect(() => {
    fetchData();
    if (user?.role === 'organizer') {
      checkProfile();
    }
  }, []); // eslint-disable-line

  const checkProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/tenant-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const p = res.data;
      // %100 tamamlanma kontrolü - completionOf ile aynı mantık
      const requiredFields = [
        p.short_name, p.full_name, p.sector, p.email,
        p.fixed_phone_number, p.address, p.city, p.country,
        p.tax_number, p.tax_office,
      ];
      const filled = requiredFields.filter(v => v && String(v).trim()).length;
      if (filled < requiredFields.length) {
        setProfileIncomplete(true);
      }
    } catch { /* sessizce geç */ }
  };

  useEffect(() => {
    if (formData.country_id) {
      const filtered = cities.filter(c => c.country_id === formData.country_id);
      setFilteredCities(filtered);
      // Reset city and venue when country changes
      setFormData(prev => ({ ...prev, city_id: '', venue_center_id: '' }));
    } else {
      setFilteredCities([]);
    }
  }, [formData.country_id, cities]);

  useEffect(() => {
    if (formData.city_id) {
      const filtered = venueCenters.filter(v => v.city_id === formData.city_id);
      setFilteredVenueCenters(filtered);
      // Reset venue when city changes
      setFormData(prev => ({ ...prev, venue_center_id: '' }));
    } else {
      setFilteredVenueCenters([]);
    }
  }, [formData.city_id, venueCenters]);

  const fetchData = async () => {
    try {
      const [countryRes, cityRes, venueRes] = await Promise.all([
        axios.get(`${API}/countries`),
        axios.get(`${API}/cities`),
        axios.get(`${API}/venue-centers`)
      ]);

      setCountries(countryRes.data);
      setCities(cityRes.data);
      setVenueCenters(venueRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        default_price_per_sqm: formData.default_price_per_sqm ? parseFloat(formData.default_price_per_sqm) : null,
        default_ceiling_height: formData.default_ceiling_height ? parseFloat(formData.default_ceiling_height) : null,
        default_max_stand_height: formData.default_max_stand_height ? parseFloat(formData.default_max_stand_height) : null,
      };
      const res = await axios.post(`${API}/exhibitions/new`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fuar başarıyla oluşturuldu!');
      navigate(`/organizer/exhibition-wizard/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fuar oluşturulamadı');
    }
  };

  return (
    <div className="p-8" data-testid="new-exhibition-page">

      {/* Profil Tamamlanmadı Modalı */}
      <Dialog open={profileIncomplete} onOpenChange={() => {}}>
        <DialogContent data-testid="profile-incomplete-modal" className="[&>button]:hidden max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Profil Tamamlanmadı
            </DialogTitle>
            <DialogDescription className="text-gray-600 pt-1">
              Yeni fuar oluşturabilmek için şirket profilinizin <strong>%100 tamamlanmış</strong> olması gerekmektedir.
              Lütfen tüm zorunlu şirket bilgilerini doldurun.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => navigate('/organizer')} className="flex-1" data-testid="profile-modal-back-btn">
              Geri Dön
            </Button>
            <Button
              onClick={() => navigate('/organizer/settings?section=tenant_profile')}
              className="flex-1 bg-teal-600 hover:bg-teal-700"
              data-testid="profile-modal-complete-btn"
            >
              <Building2 className="w-4 h-4 mr-1.5" />
              Profili Tamamla
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900">Yeni Fuar Oluştur</h1>
        <p className="text-gray-500 mt-2">Fuar bilgilerini doldurun</p>
      </div>

      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Fuar Bilgileri
          </CardTitle>
          <CardDescription>Tüm alanları eksiksiz doldurun</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="exhibition-form">
            <div className="space-y-2">
              <Label htmlFor="name">Fuar Adı *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="Örn: Uluslararası Teknoloji Fuarı 2026"
                data-testid="exhibition-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">Ülke *</Label>
              <Select
                value={formData.country_id}
                onValueChange={(value) => setFormData({...formData, country_id: value})}
                required
              >
                <SelectTrigger data-testid="country-select">
                  <SelectValue placeholder="Ülke seçin" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(country => (
                    <SelectItem key={country.id} value={country.id}>
                      {country.flag} {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">Şehir *</Label>
              <Select
                value={formData.city_id}
                onValueChange={(value) => setFormData({...formData, city_id: value})}
                required
                disabled={!formData.country_id}
              >
                <SelectTrigger data-testid="city-select">
                  <SelectValue placeholder={formData.country_id ? "Şehir seçin" : "Önce ülke seçin"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredCities.map(city => (
                    <SelectItem key={city.id} value={city.id}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="venue">Fuar Merkezi *</Label>
              <Select
                value={formData.venue_center_id}
                onValueChange={(value) => setFormData({...formData, venue_center_id: value})}
                required
                disabled={!formData.city_id}
              >
                <SelectTrigger data-testid="venue-select">
                  <SelectValue placeholder={formData.city_id ? "Fuar merkezi seçin" : "Önce şehir seçin"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredVenueCenters.map(venue => (
                    <SelectItem key={venue.id} value={venue.id}>
                      {venue.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Başlangıç Tarihi *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  required
                  className="rounded-xl"
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">Bitiş Tarihi *</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  required
                  className="rounded-xl"
                  data-testid="end-date-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Fuar hakkında kısa açıklama..."
                rows={4}
                data-testid="description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Temel Para Birimi *</Label>
              <Select
                value={formData.base_currency}
                onValueChange={(value) => setFormData({...formData, base_currency: value})}
              >
                <SelectTrigger data-testid="currency-select">
                  <SelectValue placeholder="Para birimi seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRY">₺ TRY — Türk Lirası</SelectItem>
                  <SelectItem value="USD">$ USD — Amerikan Doları</SelectItem>
                  <SelectItem value="EUR">€ EUR — Euro</SelectItem>
                  <SelectItem value="GBP">£ GBP — İngiliz Sterlini</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Bu para birimi fuardaki tüm stand fiyatlarında varsayılan olarak kullanılacak</p>
            </div>

            <div className="space-y-2">
              <Label>Varsayılan m² Birim Fiyatı</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  value={formData.default_price_per_sqm}
                  onChange={(e) => setFormData({...formData, default_price_per_sqm: e.target.value})}
                  placeholder="Örn: 800"
                  min="0"
                  step="10"
                  data-testid="default-price-input"
                />
                <span className="text-sm text-gray-500 whitespace-nowrap">{formData.base_currency}/m²</span>
              </div>
              <p className="text-xs text-gray-500">Her yeni stand oluşturulurken bu fiyat varsayılan olarak gelecek</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Genel Tavan Yüksekliği (m)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formData.default_ceiling_height}
                  onChange={(e) => setFormData({...formData, default_ceiling_height: e.target.value})}
                  placeholder="Örn: 8"
                  data-testid="default-ceiling-height-input"
                />
                <p className="text-xs text-gray-500">Tavan bölgesi atanmayan tüm alanlar için geçerli</p>
              </div>
              <div className="space-y-2">
                <Label>İzin Verilen Stand Yüksekliği (m)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formData.default_max_stand_height}
                  onChange={(e) => setFormData({...formData, default_max_stand_height: e.target.value})}
                  placeholder="Örn: 5"
                  data-testid="default-max-stand-height-input"
                />
                <p className="text-xs text-gray-500">Tavan bölgesi atanmayan standlar için maks. yükseklik</p>
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/organizer')}
                className="flex-1"
              >
                İptal
              </Button>
              <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="submit-exhibition">
                <Save className="w-4 h-4 mr-2" />
                Fuarı Oluştur
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}